import * as React from 'react';
import { ReactUtils } from '@shared/ui-react/ReactUtils';
import { Dialog, DialogUtils } from '@shared/ui-react/Dialog';
import { makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';
import { closestCenter, DndContext, DragEndEvent, MouseSensor, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@shared/ui-react/Button';

export const MapLineLabelsDialog = {
    open: (props: string[], onChange: (props: string[]) => void) => {
        const targetEl = document.getElementById('react-portal-root');
        ReactUtils.safeRender(
            <>
                <Dialog onClose={DialogUtils.close} title={__('js_map.labels.show_line_labels_on_map')}>
                    <MapLineLabelsDialogComponent props={props} onChange={onChange} />
                </Dialog>
            </>,
            targetEl
        );
    },

    close: () => {
        ReactUtils.safeUnmountComponentAtNode(document.getElementById('react-portal-root'));
    },
};

@observer
export class MapLineLabelsDialogComponent extends React.Component<{
    props: string[];
    onChange: (props: string[]) => void;
}> {
    @observable isLoading = false;
    @observable items: { value: string; title: string }[] = [];
    @observable newItem = '';
    @observable hasChanged = false;

    constructor(props: any) {
        super(props);
        makeObservable(this);

        this.items = this.props.props.map(prop => ({
            value: prop,
            title: prop,
        }));
    }

    render() {
        return (
            <>
                <div className='padded flex-column of-h'>
                    <div style={{ maxHeight: 500 }} className='of-a'>
                        <ItemsList items={this.items} onSortEnd={this.onSortEnd} onRemove={this.onRemoveItem} />
                    </div>

                    <div className='flex-row gap-half' style={{ alignItems: 'center', margin: '10px 0' }}>
                        <input
                            type='text'
                            maxLength={50}
                            placeholder={__('js_map.property_name')}
                            style={{ flex: 1 }}
                            value={this.newItem}
                            onChange={e => (this.newItem = e.target.value)}
                        />
                        <Button onClick={this.onAddItem} disabled={!this.newItem}>
                            {__('general.add')}
                        </Button>
                    </div>

                    <hr />

                    <footer className='flex-row flex-spread' style={{ width: '100%' }}>
                        <Button onClick={DialogUtils.close}>{__('general.close')}</Button>
                        <Button primary onClick={this.onSave} disabled={!this.hasChanged}>
                            {__('general.save')}
                        </Button>
                    </footer>
                </div>
            </>
        );
    }

    onSave = () => {
        this.props.onChange(this.items.map(item => item.value));
        MapLineLabelsDialog.close();
    };

    onAddItem = () => {
        this.items.push({
            value: this.newItem,
            title: this.newItem,
        });
        this.newItem = '';
        this.hasChanged = true;
    };

    onRemoveItem = (item: string) => {
        this.items = this.items.filter(i => i.value !== item);
        this.hasChanged = true;
    };

    onSortEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            const oldIndex = this.items.findIndex(item => item.value === active.id);
            const newIndex = this.items.findIndex(item => item.value === over.id);

            const columns = [...this.items];
            this.items = arrayMove(columns, oldIndex, newIndex);
        }
        this.hasChanged = true;
    };
}

const ItemsList = observer(({ items, onSortEnd, onRemove }) => {
    const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));

    return (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onSortEnd} modifiers={[restrictToVerticalAxis, restrictToParentElement]}>
            <SortableContext items={items.map(item => item.value)} strategy={verticalListSortingStrategy}>
                <div>
                    {items.map(item => (
                        <SortableListItem key={`item-${item.value}`} id={item.value} item={item} onRemove={onRemove} />
                    ))}
                </div>
            </SortableContext>
        </DndContext>
    );
});

const ListItem = observer(({ item, dragHandleProps = null, style = {}, onRemove }) => (
    <div className='sortable-list__item' style={{ padding: 0, ...style }}>
        <div className='sortable__drag-handle' {...dragHandleProps}>
            ፧
        </div>
        <label className='flex-row gap-half checkbox-label' style={{ width: '100%', justifyContent: 'space-between' }}>
            <span>{item.title}</span>
            <Button secondary subtle small icon='iki iki-cross' onClick={() => onRemove(item.value)} />
        </label>
    </div>
));

const SortableListItem = observer(({ id, item, onRemove }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    const dragHandleProps = {
        ...attributes,
        ...listeners,
    };

    return (
        <div ref={setNodeRef}>
            <ListItem item={item} dragHandleProps={dragHandleProps} style={style} onRemove={onRemove} />
        </div>
    );
});
